/**
 * Beat 12's dawn crew: the driver and the loader (`docs/beat-12-plan.md`,
 * stage 3). Pure fixed-step simulation — no meshes, no audio — so the clock
 * the beat is built on replays in the sim lane exactly as it plays.
 *
 * **The clock is a man, not a timer.** Mark 1 sends the driver from the ramp
 * foot to the junction box; he works there for `DRIVER_RESET_SECONDS`; the
 * floods come back on the step his work ends, not on a countdown that happens
 * to agree with him (plan gate 4: hold him on his route and the floods must
 * never return). That is `culvertQueue`'s pattern — the queue and the clock —
 * with a different service time, as the act plan says it would be.
 *
 * The cones are the stealth system's `Watcher`s, mutated in place every step
 * (the stealth system holds these exact objects; a copy would leave it
 * sampling the cone they had at construction).
 */

import type { Watcher } from '@/systems/stealth';
import { Worker, type WorkerRoute, type WorkerState } from '@/systems/worker';
import { RACE_GATE_AT, YARD_DAWN_JUNCTION_BOX } from '@/world/loadingYard';

/**
 * How long the driver works at the box before the floods come back. **The one
 * tuning constant this beat has** (the plan), set by `beat-12-stopwatch.spec.ts`
 * and not by feel (D-198, D-205). With his 27 s walk the floods return ~41 s
 * after mark 1. On the route that plays — under the trailer, F on arrival —
 * the herd's head reaches the vehicle gate ~31 s after mark 1 in the real room
 * (~10 s of margin); one capture's cost (~14 s) puts her past the clock; a
 * roll wins it back. It was 17 s, timed on a route round the cab that the
 * driver walks into her on and that the sim lane could not see was unplayable
 * (audit row 24): on the trailer route 17 s let a trotting player who had been
 * caught once still beat the floods, so a capture cost nothing (D-210). The
 * plan's "a walk gets two-thirds of the way" could not be kept — the lane to
 * the gate is 10 m, so a walk and a roll arrive ~2 s apart there.
 */
export const DRIVER_RESET_SECONDS = 14;

/**
 * The crew stand at the ramp foot, which is the race's north end, either side
 * of the race gate: the driver west of it, north of the trailer's rear; the
 * loader east of it. The driver stands back from the gate's west side, 2 m
 * off Holly's hold station (`HOLLY_HOLD_STATION`): at 1.5 m, where he first
 * stood, a player who did mark 2 first had a sheep rearing at his elbow.
 * Both watch the gate — which is where she has to be for
 * mark 2, and why mark 1 comes first unless she is careful.
 */
export const DRIVER_POST = { x: 5.0, z: 8.6 } as const;
export const LOADER_POST = { x: 10.3, z: 7.0 } as const;

/**
 * The driver's way to the box: the long way round, because the race runs from
 * the shed wall to the gate and the trailer and cab fill the yard's middle. West
 * along the north side of the truck, south past its cab (clear of the smallest
 * pallet stack by his own half-width), then east along the wall. 34 m, about
 * 27 s at his pace: the walk is half the clock, and she can hear all of it.
 * He comes back the same way (a `Worker` returns straight to park, so the
 * corners are waypoints both ways).
 */
const CORNER_NORTH = { x: -7.9, z: 7.9 } as const;
const CORNER_SOUTH = { x: -7.9, z: -6.0 } as const;

/** Where the driver stands to work the box: in front of it, facing the wall. */
const BOX_STAND = { x: YARD_DAWN_JUNCTION_BOX.x + 0.15, z: YARD_DAWN_JUNCTION_BOX.z + 0.75 } as const;

/** Facing convention: 0 faces +Z, increasing toward +X (`Watcher.facing`). The wall is −Z of the box. */
const FACE_WALL = Math.PI;

/** Metres per second: a man walking with purpose, not hurrying. */
export const DRIVER_SPEED = 1.25;

/** The box's index in the driver's waypoints: the one dwell that is the clock. */
const BOX_WAYPOINT = 2;

function bearing(from: { x: number; z: number }, to: { x: number; z: number }): number {
  return Math.atan2(to.x - from.x, to.z - from.z);
}

/**
 * The cones: 10 m and ±25°, eyes at a standing man's height. Narrower than
 * beat 2's ±45° on purpose, and the geometry is why: from the loader's post
 * the gate and the box are both south-west of him, only 26° apart, so any
 * wider cone would still hold the gate after he turns to the box — and the
 * plan's opening (*"the race side is not"* watched) would not exist.
 */
const CONE = { coneHalfAngle: Math.PI * 0.14, range: 10, eyeHeight: 1.6 } as const;

/**
 * The driver's cone is beat 2's ±45°, not the loader's ±25°. The narrow cone
 * is argued from the LOADER's post and nothing about it applies to a man
 * walking the yard with a torch; given to him too, it made him blind — a pig
 * standing in the open 3.3 m ahead and 0.6 m off his line scored 0.35 on angle
 * and was never caught (measured, session 67; D-204).
 */
const DRIVER_CONE = { ...CONE, coneHalfAngle: Math.PI * 0.25 } as const;

const GATE = { x: RACE_GATE_AT.x, z: RACE_GATE_AT.z } as const;

/**
 * The driver's round: out to the box, work it, back to the ramp foot. A
 * `Worker` round is park → waypoints → park, which is exactly this, and his
 * `restBetweenRounds` is effectively forever: he goes once.
 */
function driverRoute(speed: number): WorkerRoute {
  return {
    park: { ...DRIVER_POST },
    speed,
    turnRate: 3.2,
    stride: 0.72,
    waypoints: [
      { ...CORNER_NORTH, dwell: 0 },
      { ...CORNER_SOUTH, dwell: 0 },
      { x: BOX_STAND.x, z: BOX_STAND.z, dwell: DRIVER_RESET_SECONDS, facing: FACE_WALL },
      { ...CORNER_SOUTH, dwell: 0 },
      { ...CORNER_NORTH, dwell: 0 },
    ],
    restBetweenRounds: 1e9,
  };
}

export type CrewPhase = 'loading' | 'to-the-box' | 'at-the-box' | 'back';

export class YardDawnCrew {
  readonly driver: Worker;
  readonly driverWatcher: Watcher;
  readonly loaderWatcher: Watcher;
  #sent = false;
  #floodsBack = false;
  #wasAtBox = false;
  /** Sent again while he was still walking home from the last reset: he goes when he gets there. */
  #pending = false;

  /**
   * `driverSpeed` exists for plan gate 4's fixture: a driver held on his route
   * (walking at a crawl) must never bring the floods back, and a slower one
   * must bring them back later by exactly his extra walk. A countdown that
   * merely agreed with him would pass neither.
   */
  constructor(options: { driverSpeed?: number } = {}) {
    this.driver = new Worker(driverRoute(options.driverSpeed ?? DRIVER_SPEED));
    this.driverWatcher = { id: 'dawn.driver', position: { x: DRIVER_POST.x, y: 0, z: DRIVER_POST.z }, facing: bearing(DRIVER_POST, GATE), ...DRIVER_CONE };
    this.loaderWatcher = { id: 'dawn.loader', position: { x: LOADER_POST.x, y: 0, z: LOADER_POST.z }, facing: bearing(LOADER_POST, GATE), ...CONE };
  }

  get watchers(): readonly Watcher[] {
    return [this.driverWatcher, this.loaderWatcher];
  }

  /** True from the step the driver finishes at the box until the lights are cut again (`rearm`, then `send`). */
  get floodsBack(): boolean {
    return this.#floodsBack;
  }

  get phase(): CrewPhase {
    if (!this.#sent) return this.#floodsBack ? 'back' : 'loading';
    if (this.#floodsBack) return 'back';
    return this.#atBox() ? 'at-the-box' : 'to-the-box';
  }

  /** Seconds of work left at the box, or null when he is not working it. */
  get resetRemaining(): number | null {
    return this.#atBox() ? this.driver.state.dwellRemaining : null;
  }

  #atBox(): boolean {
    const state = this.driver.state;
    return state.phase === 'dwelling' && state.waypoint === BOX_WAYPOINT;
  }

  /**
   * Mark 1 happened: the driver goes to the box and the loader turns to watch
   * him go. Idempotent until `rearm()`. A driver still walking home from the
   * last reset goes out again the step he reaches his post — `Worker.restart`
   * refuses a walking man, because it would teleport him.
   *
   * **The loader's cone turns to the box and off the race**, and that is the
   * opening the plan is built on: *"while he walks, the junction-box side of
   * the yard is watched and the race side is not."*
   */
  send(): void {
    if (this.#sent) return;
    this.#sent = true;
    this.#floodsBack = false;
    if (this.driver.state.phase === 'parked') this.driver.restart();
    else this.#pending = true;
    this.loaderWatcher.facing = bearing(LOADER_POST, BOX_STAND);
  }

  /**
   * The floods came back before the herd moved (D-205 review): the box can be
   * cut again. He finishes his walk home either way; the next `send()` sends
   * him out again. Only legal once the floods are back.
   */
  rearm(): void {
    if (!this.#floodsBack) return;
    this.#sent = false;
    this.#pending = false;
  }

  /** One fixed step. Returns the driver's state so the presenter can pose and voice him. */
  step(dt: number): Readonly<WorkerState> {
    if (this.#pending && this.driver.state.phase === 'parked') {
      this.#pending = false;
      this.driver.restart();
    }
    // A never-started worker does not move on `step`, so this is inert until mark 1.
    const state = this.driver.step(dt);
    this.driverWatcher.position.x = state.x;
    this.driverWatcher.position.z = state.z;
    // Parked at the ramp foot he watches the gate, not the way he will walk.
    this.driverWatcher.facing = state.phase === 'parked' ? bearing(DRIVER_POST, GATE) : state.facing;
    // The clock: the step his work at the BOX ends is the step the floods come
    // back. Keyed on that waypoint, not on "stopped dwelling": his corners
    // are zero-length dwells, and a step can end on one, which read as the
    // reset finishing before he had reached the box.
    const atBox = this.#atBox();
    if (this.#wasAtBox && !atBox) {
      this.#floodsBack = true;
      // Back on, and the loader turns back to the gate he is loading through.
      this.loaderWatcher.facing = bearing(LOADER_POST, GATE);
    }
    this.#wasAtBox = atBox;
    return state;
  }
}
